import React, { useState } from "react";
import { X, ShoppingCart, Package, Search, ArrowLeft } from "lucide-react";
import { formatPrice } from "../../utils/functions.js";
import { createDepositRequestBuyer } from "../../services/buyerDepositRequestServices.ts";
import { useToast } from "../../context/ToastContext.jsx";

const WithdrawOrders = ({ onClose, activePackage = null }) => {
  const [remarks, setRemarks] = useState("");
  const [confirmCancellation, setConfirmCancellation] = useState(false);
  const { showSuccess, showError } = useToast();

  const handleSubmit = async () => {
    try {
      const response = await createDepositRequestBuyer({
        notes: remarks,
      });
      onClose();
      showSuccess("Withdraw request submitted successfully");
    } catch (error) {
      console.error("Error creating deposit request:", error);
      showError(
        "Failed to submit withdraw request" +
          " : " +
          error.response?.data?.message
      );
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header Section */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Withdraw Deposit</h1>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-200 rounded-full transition-colors"
        >
          <X className="w-6 h-6 text-gray-600" />
        </button>
      </div>

      {/* Main Content Area - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Side - Description Section */}
        <div className="space-y-6">
          {/* Important Notice */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 rounded-xl p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-xl font-bold text-red-800 mb-3">
                  ⚠️ Important Notice: Package Cancellation
                </h3>
                <div className="text-sm text-red-700 space-y-3">
                  <p className="font-semibold text-base">
                    Withdrawing your deposit means you are{" "}
                    <strong className="text-red-900">
                      cancelling your package
                    </strong>{" "}
                    with Khat Al Jazeera Cars Auction.
                  </p>
                  <p className="font-medium">This action will:</p>
                  <ul className="list-disc list-inside ml-4 space-y-2">
                    <li>Terminate your active package immediately</li>
                    <li>Remove your bidding privileges and access</li>
                    <li>Refund your deposit amount to your account</li>
                    <li>Cancel any pending package requests</li>
                  </ul>
                  <p className="font-bold text-red-900 mt-4 p-3 bg-red-100 rounded-lg">
                    Please ensure you want to cancel your package before
                    proceeding with the withdrawal.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form Section */}
        <div className="lg:sticky lg:top-6">
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-2">
                Your Deposit Amount
              </h3>
              <div className="text-4xl font-bold text-orange-600">
                {activePackage &&
                (activePackage.depositAmount || activePackage.deposit)
                  ? formatPrice(
                      activePackage.depositAmount || activePackage.deposit
                    )
                  : formatPrice(0)}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remarks
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={4}
                placeholder="Enter any remarks..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Confirmation Checkbox */}
            <div className="mb-6">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmCancellation}
                  onChange={(e) => setConfirmCancellation(e.target.checked)}
                  className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  <strong className="text-red-600">I understand</strong> that
                  withdrawing my deposit will
                  <strong className="text-red-600">
                    {" "}
                    cancel my package
                  </strong>{" "}
                  and remove all my bidding privileges. I confirm that I want to
                  proceed with this action.
                </span>
              </label>
            </div>

            <button
              onClick={() => {
                handleSubmit();
              }}
              disabled={!confirmCancellation}
              className={`w-full px-6 py-3 rounded-lg transition-colors font-medium ${
                confirmCancellation
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              {confirmCancellation
                ? "Cancel Package & Withdraw Deposit"
                : "Confirm Cancellation to Proceed"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawOrders;
